import logo from 'assets/logo-header.png';
import GerenciamentoMensagensContainer from 'components/mensagens/GerenciamentoMensagensContainer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Container, Nav, Navbar } from 'react-bootstrap';
import { GoBell, GoCalendar, GoPerson, GoSearch, GoSignOut } from 'react-icons/go';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { selectFunc, signOutFunc } from 'redux/features/auth/authSlice';
import { getMensagens } from 'services/mensagens';
import styles from 'styles/styles.module.css';

export default function CustomNavbar() {
  const dispatch = useDispatch();
  const func = useSelector(selectFunc);
  const [naoLidas, setNaoLidas] = useState(0);
  const [codFuncDestino, setCodFuncDestino] = useState('');
  const location = useLocation();
  const [showGerenciamentoModal, setShowGerenciamentoModal] = useState(false);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);
  const getLinkClassName = (path) =>
    [styles.navLinkHover, isActive(path) ? styles.navLinkActive : ''].filter(Boolean).join(' ');

  const contextoMensagens = useMemo(
    () => ({
      usuarioLogado: {
        tipo: func ? 'func' : '',
        codFunc: func?.codFunc ? String(func.codFunc) : '',
      },
    }),
    [func]
  );

  const signOut = () => {
    dispatch(signOutFunc());
  };

  useEffect(() => {
    if (func?.codFunc) {
      setCodFuncDestino(String(func.codFunc));
    } else {
      setCodFuncDestino('');
    }
  }, [func?.codFunc]);

  // Busca mensagens não lidas
  const fetchNaoLidas = useCallback(async () => {
    if (!codFuncDestino || document.visibilityState === 'hidden') return;

    try {
      const mensagens = await getMensagens({ codfunc: codFuncDestino });

      if (Array.isArray(mensagens)) {
        const pendentes = mensagens.filter((m) => {
          const naoLida = String(m.LEITURA || m.leitura).toUpperCase() === 'N';
          const ehParaMim = String(m.CODFUNCDESTINO || m.codFuncDestino) === String(codFuncDestino);
          return naoLida && ehParaMim;
        });

        setNaoLidas(pendentes.length);
      } else {
        setNaoLidas(0);
      }
    } catch {
      setNaoLidas(0);
    }
  }, [codFuncDestino]);

  useEffect(() => {
    fetchNaoLidas();

    const id = setInterval(fetchNaoLidas, 300_000); // 5 minutos

    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchNaoLidas();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchNaoLidas]);

  const handleOpenModal = () => {
    setShowGerenciamentoModal(true);
  };

  const handleCloseModal = () => {
    setShowGerenciamentoModal(false);
    fetchNaoLidas();
  };

  return (
    <>
      <Navbar bg="white" expand="lg" className={`border-bottom ${styles.homePageFonts}`}>
        <Container fluid className="px-2 px-md-3 py-0">
          <Navbar.Brand className="me-2 me-sm-3 ms-1 ms-lg-0">
            <Link to="/">
              <img src={logo} alt="Prevencor Marília" style={{ height: '45px' }} />
            </Link>
          </Navbar.Brand>

          <Navbar.Toggle aria-controls="basic-navbar-nav" className="border-0 p-1 shadow-none" />

          <Navbar.Collapse id="basic-navbar-nav" className="order-3 order-lg-0 w-100">
            <Nav className="gap-1 my-2 my-lg-0">
              <Nav.Link
                as={Link}
                to="/agenda"
                className={`${styles.navLinkHover} ${getLinkClassName('/agenda')}`}
                aria-current={isActive('/agenda') ? 'page' : undefined}
                style={{
                  color: 'var(--brand-blue-500)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  lineHeight: 1,
                }}
              >
                <GoCalendar size={16} /> Agenda
              </Nav.Link>

              <Nav.Link
                as={Link}
                to="/pacientes"
                className={`${styles.navLinkHover} ${getLinkClassName('/pacientes')}`}
                aria-current={isActive('/pacientes') ? 'page' : undefined}
                style={{
                  color: 'var(--brand-blue-500)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  lineHeight: 1,
                }}
              >
                <GoSearch size={16} /> Localizar
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>

          <div className="d-flex align-items-center ms-auto gap-2 order-2">
            {/* BOTÃO DE NOTIFICAÇÃO / MENSAGENS REFEITO */}
            <Nav.Link
              as="button"
              type="button"
              className={`${styles.navLinkHover} btn btn-link p-1 text-decoration-none`}
              onClick={handleOpenModal}
              title={naoLidas > 0 ? `${naoLidas} mensagem(ns) não lida(s)` : 'Acessar mensagens'}
              style={{
                color: 'var(--brand-blue-500)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                lineHeight: 1,
                position: 'relative',
              }}
            >
              <div className="position-relative d-flex align-items-center">
                <GoBell size={21} className={naoLidas > 0 ? 'text-primary' : ''} />

                {/* Ponto indicador vermelho piscante no ícone */}
                {naoLidas > 0 && (
                  <span
                    className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle"
                    style={{ marginTop: '2px', marginLeft: '-2px' }}
                  >
                    <span className="visually-hidden">Novas mensagens</span>
                  </span>
                )}
              </div>

              {/* Texto descritivo para telas pequenas e médias */}
              <span className="d-none d-md-inline fw-semibold" style={{ fontSize: '0.9rem' }}>
                Mensagens
              </span>

              {/* Badge em destaque com cor de alerta/perigo (Vermelho) */}
              {naoLidas > 0 && (
                <Badge
                  pill
                  bg="danger"
                  className="px-2 py-1 shadow-sm fw-bold"
                  style={{
                    fontSize: '0.75rem',
                    letterSpacing: '0.02em',
                  }}
                >
                  {naoLidas > 99 ? '99+' : naoLidas}
                </Badge>
              )}
            </Nav.Link>

            <div
              className="vr align-self-center mx-1"
              style={{ color: 'var(--brand-blue-500)', height: '1.5rem' }}
            ></div>

            <Nav.Link
              as={Link}
              to="/perfil-funcionario"
              className={`${styles.navLinkHover}`}
              aria-current={isActive('/perfil-funcionario') ? 'page' : undefined}
              style={{
                color: 'var(--brand-blue-500)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                lineHeight: 1,
              }}
            >
              <GoPerson size={18} className="flex-shrink-0" />
              <span className="d-none d-sm-inline text-nowrap">{func?.nome}</span>
            </Nav.Link>

            <Nav.Link
              as="button"
              type="button"
              className={`${styles.navLinkHover}`}
              onClick={signOut}
              style={{
                color: 'var(--brand-blue-500)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                lineHeight: 1,
              }}
            >
              <GoSignOut size={16} />
              <span className="d-none d-sm-inline">Sair</span>
            </Nav.Link>
          </div>
        </Container>
      </Navbar>

      <GerenciamentoMensagensContainer
        show={showGerenciamentoModal}
        onHide={handleCloseModal}
        contexto={contextoMensagens}
        codFuncDestino={codFuncDestino}
      />
    </>
  );
}
